/**
 * Photos Store
 *
 * Tracks uploaded photos with metadata.
 * Photos are associated with drops and are deleted when the parent drop is deleted.
 *
 */

export interface Photo {
  id: string
  dropId: string
  filename: string
  uploadedAt: Date
  uploadedBy: string // session ID or contributor name
  caption?: string
  size: number // in bytes
  thumbnailUrl?: string // Base64 data URL for thumbnail
  originalUrl?: string // Base64 data URL for original (or URL to storage)
}

const PHOTOS_STORAGE_KEY = 'photodrop_photos'

// Serialize photos for localStorage
function serializePhotos(photos: Photo[]): string {
  return JSON.stringify(photos.map(p => ({
    ...p,
    uploadedAt: p.uploadedAt.toISOString(),
  })))
}

// Deserialize photos from localStorage
function deserializePhotos(data: string): Photo[] {
  try {
    const parsed = JSON.parse(data)
    return parsed.map((p: Record<string, unknown>) => ({
      ...p,
      uploadedAt: new Date(p.uploadedAt as string),
    }))
  } catch {
    return []
  }
}

// Get all photos
export function getPhotos(): Photo[] {
  const stored = localStorage.getItem(PHOTOS_STORAGE_KEY)
  if (stored) {
    return deserializePhotos(stored)
  }
  return []
}

// Save photos to storage
export function savePhotos(photos: Photo[]): void {
  localStorage.setItem(PHOTOS_STORAGE_KEY, serializePhotos(photos))
}

// Get photos for a specific drop
export function getPhotosByDropId(dropId: string): Photo[] {
  return getPhotos().filter(p => p.dropId === dropId)
}

// Get a single photo by ID
export function getPhotoById(id: string): Photo | null {
  const photos = getPhotos()
  return photos.find(p => p.id === id) || null
}

// Add a new photo
export function addPhoto(photo: Omit<Photo, 'id' | 'uploadedAt'>): Photo {
  const photos = getPhotos()
  const newPhoto: Photo = {
    ...photo,
    id: `photo-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
    uploadedAt: new Date(),
  }
  photos.push(newPhoto)
  savePhotos(photos)

  return newPhoto
}

// Add multiple photos at once
export function addPhotos(photosToAdd: Omit<Photo, 'id' | 'uploadedAt'>[]): Photo[] {
  if (photosToAdd.length === 0) return []

  const photos = getPhotos()
  const newPhotos: Photo[] = photosToAdd.map((p, index) => ({
    ...p,
    id: `photo-${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`,
    uploadedAt: new Date(),
  }))
  photos.push(...newPhotos)
  savePhotos(photos)

  return newPhotos
}

// Delete a single photo
export function deletePhoto(id: string): boolean {
  const photos = getPhotos()
  const photoToDelete = photos.find(p => p.id === id)
  if (!photoToDelete) return false

  const filtered = photos.filter(p => p.id !== id)
  savePhotos(filtered)

  return true
}

// Delete all photos for a drop (cascade delete)
export function deletePhotosByDropId(dropId: string): number {
  const photos = getPhotos()
  const before = photos.length
  const filtered = photos.filter(p => p.dropId !== dropId)
  const deletedCount = before - filtered.length

  if (deletedCount > 0) {
    savePhotos(filtered)
  }

  return deletedCount
}

// Check if a photo exists
export function photoExists(id: string): boolean {
  return getPhotoById(id) !== null
}

// Get photo count for a drop
export function getPhotoCountForDrop(dropId: string): number {
  return getPhotosByDropId(dropId).length
}

// Clear all photos (for testing)
export function clearAllPhotos(): void {
  localStorage.removeItem(PHOTOS_STORAGE_KEY)
}
